import type { AccessLevel, ResolvedDomain } from './accessTypes';

/**
 * The access ladder and the comparisons the UI needs.
 *
 * The ladder is defined once, here, as an ordered array — comparisons go through {@link atLeastLevel}
 * rather than comparing strings or indexes at call sites, so a future level inserted in the middle is a
 * one-line change instead of a hunt through the codebase for `=== 'ADMIN'`.
 */

/** Every level, weakest first. The order IS the ladder. */
export const ACCESS_LEVELS: readonly AccessLevel[] = ['NONE', 'VIEW', 'EDIT', 'ADMIN'] as const;

/** The levels a merchant administrator picks from in the access matrix (i.e. all of them). */
export const SELECTABLE_ACCESS_LEVELS = ACCESS_LEVELS;

const RANK: Record<AccessLevel, number> = { NONE: 0, VIEW: 1, EDIT: 2, ADMIN: 3 };

/** True when `level` sits at or above `required` on the ladder. */
export function atLeastLevel(level: AccessLevel | undefined, required: AccessLevel): boolean {
  if (!level) {
    return required === 'NONE';
  }
  return RANK[level] >= RANK[required];
}

/**
 * The higher of two levels — the combination rule the backend applies across a user's groups
 * (adr-merchant-portal-012). Mirrored here so the UI can preview the effect of a change before saving it,
 * without a round trip.
 */
export function maxLevel(a: AccessLevel | undefined, b: AccessLevel | undefined): AccessLevel {
  if (!a) return b ?? 'NONE';
  if (!b) return a;
  return RANK[a] >= RANK[b] ? a : b;
}

/** True when the domain confers nothing — the portal hides it entirely rather than showing it disabled. */
export function isNoAccess(level: AccessLevel | undefined): boolean {
  return !level || level === 'NONE';
}

/** True when the subject may change the domain's business data. */
export function canEdit(level: AccessLevel | undefined): boolean {
  return atLeastLevel(level, 'EDIT');
}

/** True when the subject may change the domain's *settings*, not just its data. */
export function canAdminister(level: AccessLevel | undefined): boolean {
  return atLeastLevel(level, 'ADMIN');
}

/** The resolved level for a domain, or `NONE` when the domain is absent from the resolution. */
export function levelOf(domains: ResolvedDomain[] | undefined, domainCode: string): AccessLevel {
  return domains?.find((domain) => domain.domainCode === domainCode)?.level ?? 'NONE';
}
